import type { PetitionMarqueeCardData } from '../usePetitionMarquee';

import { MAX_CARDS_PER_ROW, MIN_CARDS_PER_ROW } from './rowSize';

export type MarqueeRows = {
	/** The always-visible line (the only one on desktop). */
	main: readonly PetitionMarqueeCardData[];
	/** The mobile-only top line. */
	secondary: readonly PetitionMarqueeCardData[];
};

/**
 * Cycles the pool until the row is full, so short curations still fill a row.
 * MarqueeTrack renders the repeats as duplicates (aria-hidden, untabbable, still clickable).
 */
function fillRow(pool: readonly PetitionMarqueeCardData[], size: number): readonly PetitionMarqueeCardData[] {
	return Array.from({ length: size }, (_, index) => pool[index % pool.length]).filter((card) => card !== undefined);
}

/**
 * Picks which petitions each marquee line shows: a same-size slice per line, disjoint between
 * the lines whenever the curation is big enough. The slices follow the FCM order, so curators
 * control both which petitions appear and which line they land on.
 */
export function selectMarqueeRows(petitions: readonly PetitionMarqueeCardData[]): MarqueeRows {
	if (petitions.length === 0) return { main: [], secondary: [] };

	const rowSize = Math.max(MIN_CARDS_PER_ROW, Math.min(MAX_CARDS_PER_ROW, Math.floor(petitions.length / 2)));
	const mainPool = petitions.slice(0, rowSize);
	// Prefer petitions the main row isn't showing; a curation smaller than one row can't avoid
	// repeating across rows, so it falls back to the whole list - rotated by half so the two
	// lines at least don't read as mirrored copies.
	const secondaryPool =
		petitions.length > rowSize
			? petitions.slice(rowSize, rowSize * 2)
			: [...petitions.slice(Math.ceil(petitions.length / 2)), ...petitions.slice(0, Math.ceil(petitions.length / 2))];

	return {
		main: fillRow(mainPool, rowSize),
		secondary: fillRow(secondaryPool, rowSize),
	};
}
